import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { authToken } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/omniread/omniread_logo.webp';

export default function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            toast.error('Please enter both email and password');
            return;
        }

        setLoading(true);
        try {
            const resp = await axios.post(`${API}/auth/login`, {
                email,
                password
            });

            // Store the access token and set default authorization header
            if (resp.data.success && resp.data.accessToken) {
                authToken.set(resp.data.accessToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.accessToken}`;

                // Check if profile exists after successful login
                const profileResp = await axios.get(`${API}/profile`);
                if (profileResp.data) {
                    navigate('/dashboard');
                } else {
                    navigate('/profile-setup');
                }
            } else {
                toast.error('Invalid response from server');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Login failed. Please check your credentials.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (clientId && window.google) {
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: handleGoogleResponse,
                auto_select: false,
                itp_support: true
            });
            window.google.accounts.id.renderButton(
                document.getElementById("googleSignInDiv"),
                {
                    theme: "filled_black",
                    size: "large",
                    width: "100%",
                    text: "continue_with",
                    shape: "rectangular",
                    logo_alignment: "left"
                }
            );
        }
    }, []);

    const handleGoogleResponse = async (response) => {
        setLoading(true);
        try {
            const resp = await axios.post(`${API}/auth/google`, {
                token: response.credential
            });

            if (resp.data.success && resp.data.accessToken) {
                authToken.set(resp.data.accessToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${resp.data.accessToken}`;

                // Check if profile exists after successful login
                const profileResp = await axios.get(`${API}/profile`);
                if (profileResp.data) {
                    navigate('/dashboard');
                } else {
                    navigate('/profile-setup');
                }
            } else {
                toast.error('Invalid response from server');
                setLoading(false);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Login failed. Please check your credentials.');
            console.error(err);
            setLoading(false);
        }
    };

    return (
        <div className="page-container flex flex-col items-center justify-center min-h-screen p-4 md:p-12 overflow-x-hidden">
            <div className="fade-in max-w-[450px] w-full">
                {/* Logo */}
                <div className="mb-8 cursor-pointer text-center" onClick={() => navigate('/')}>
                    <img
                        src={LOGO_URL}
                        alt="OmniRead.AI"
                        className="w-24 h-24 md:w-32 md:h-32 mx-auto block drop-shadow-xl"
                    />
                </div>

                <h1 className="gold-text mb-2 text-3xl md:text-5xl font-bold tracking-tighter text-center uppercase">
                    Welcome Back
                </h1>

                <p className="text-base text-gray-400 mb-8 text-center tracking-wide">
                    Continue your journey of wisdom
                </p>

                <div className="mystical-card">
                    <form onSubmit={handleLogin}>
                        <div className="mb-6">
                            <Label htmlFor="email" className="block mb-2">
                                Email
                            </Label>
                            <div className="relative">
                                <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    className="pl-12"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <Label htmlFor="password" className="block mb-2">
                                Password
                            </Label>
                            <div className="relative">
                                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="pl-12"
                                />
                            </div>
                        </div>

                        <div className="text-right mb-6">
                            <Link
                                to="/forgot-password"
                                className="text-[#d4af37] text-sm no-underline hover:text-[#f4d03f] transition-colors"
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            className="btn-gold w-full mb-6"
                            disabled={loading}
                        >
                            <LogIn size={20} className="mr-2" />
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </form>

                    <div className="relative text-center mb-6">
                        <div className="absolute top-1/2 left-0 right-0 h-px bg-gold/20"></div>
                        <span className="relative bg-[#1a1a1a]/80 px-4 text-gray-500 text-sm">
                            OR
                        </span>
                    </div>

                    <div className="w-full flex justify-center">
                        <div id="googleSignInDiv"></div>
                    </div>
                </div>

                <p className="text-center mt-6 text-gray-500 text-base">
                    Don't have an account?{' '}
                    <Link
                        to="/signup"
                        className="text-[#d4af37] no-underline font-bold hover:text-[#f4d03f] transition-colors"
                    >
                        Sign Up
                    </Link>
                </p>

                <div className="mt-12 text-center">
                    <p className="text-[#666] text-sm">
                        © {new Date().getFullYear()} OmniRead.AI. All rights reserved. •{' '}
                        <Link to="/privacy" className="hover:text-[#d4af37] transition-colors">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
